// Tranche 6 (white-label) — Google Identity Services (GIS) session.
//
// A lightweight Google sign-in path that sits ALONGSIDE MSAL. Entirely
// inert unless VITE_GOOGLE_CLIENT_ID is set — the script never loads and
// no Google UI appears, so the Microsoft-only flow is unchanged.
//
// Flow: GIS returns a Google ID token (a JWT, audience = our client id,
// ~1h expiry) in a callback. We keep it in localStorage and hand it to the
// backend as the Bearer token; the backend verifies it (services/
// googleAuth.ts). On expiry getGoogleToken() returns null, which makes the
// app fall back to the sign-in screen — the user clicks Google again.
// (Silent renewal via GIS/FedCM can be layered on later; a clean re-auth
// is the safe v1.)

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim() || '';
const STORAGE_KEY = 'boaz_google_id_token';
const GIS_SRC = 'https://accounts.google.com/gsi/client';

export function googleEnabled(): boolean {
  return !!GOOGLE_CLIENT_ID;
}

export interface GoogleSession {
  credential: string;   // the raw ID token (JWT) for API calls
  sub: string;
  email: string | null;
  name: string | null;
  exp: number;          // unix seconds
}

// ─── Decode (no verification — the BACKEND verifies; this is just to read
// exp/email for UI + expiry checks). Base64url-safe. ──────────────────────
function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

function sessionFromToken(token: string): GoogleSession | null {
  const p = decodeJwt(token);
  if (!p || typeof p.sub !== 'string' || typeof p.exp !== 'number') return null;
  return {
    credential: token,
    sub: p.sub,
    email: typeof p.email === 'string' ? p.email : null,
    name: typeof p.name === 'string' ? p.name : null,
    exp: p.exp,
  };
}

// ─── Module store + React subscription ────────────────────────────────────
let current: GoogleSession | null = (() => {
  if (!googleEnabled()) return null;
  const t = localStorage.getItem(STORAGE_KEY);
  if (!t) return null;
  const s = sessionFromToken(t);
  // Drop an already-expired token on load.
  if (!s || s.exp * 1000 <= Date.now()) { localStorage.removeItem(STORAGE_KEY); return null; }
  return s;
})();

const listeners = new Set<() => void>();
function emit(): void { listeners.forEach((l) => l()); }

export function subscribeGoogle(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function getGoogleSession(): GoogleSession | null {
  return current;
}

function setSession(token: string | null): void {
  if (!token) {
    current = null;
    localStorage.removeItem(STORAGE_KEY);
  } else {
    const s = sessionFromToken(token);
    if (!s) return;
    current = s;
    localStorage.setItem(STORAGE_KEY, token);
  }
  emit();
}

/** Valid token for API calls, or null when expired/absent (→ re-auth). */
export function getGoogleToken(): string | null {
  if (!current) return null;
  if (current.exp * 1000 <= Date.now() + 60_000) { // 60s skew
    setSession(null);
    return null;
  }
  return current.credential;
}

export function signOutGoogle(): void {
  setSession(null);
  try {
    (window as unknown as { google?: { accounts?: { id?: { disableAutoSelect?: () => void } } } })
      .google?.accounts?.id?.disableAutoSelect?.();
  } catch { /* GIS not loaded — nothing to disable */ }
}

// ─── GIS script loading + init ────────────────────────────────────────────
let gisPromise: Promise<void> | null = null;

interface GisId {
  initialize: (cfg: Record<string, unknown>) => void;
  renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
  prompt: () => void;
  disableAutoSelect: () => void;
}
function gis(): GisId | null {
  return (window as unknown as { google?: { accounts?: { id?: GisId } } }).google?.accounts?.id ?? null;
}

export function loadGoogle(): Promise<void> {
  if (!googleEnabled()) return Promise.reject(new Error('Google sign-in not configured'));
  if (gisPromise) return gisPromise;
  gisPromise = new Promise<void>((resolve, reject) => {
    if (gis()) return resolve();
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google sign-in script'));
    document.head.appendChild(s);
  });
  return gisPromise;
}

let initialized = false;
function ensureInit(): void {
  const id = gis();
  if (!id || initialized) return;
  id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (resp: { credential?: string }) => {
      if (resp?.credential) setSession(resp.credential);
    },
    auto_select: false,
    cancel_on_tap_outside: true,
    use_fedcm_for_prompt: true,
  });
  initialized = true;
}

/** Render the official Google button into `el`. Loads + inits GIS on demand. */
export async function renderGoogleButton(el: HTMLElement): Promise<void> {
  await loadGoogle();
  ensureInit();
  const id = gis();
  if (!id) throw new Error('Google sign-in unavailable');
  id.renderButton(el, { theme: 'outline', size: 'large', width: 320, text: 'signin_with' });
}
